import { Key, useCallback } from 'react';

import { format } from 'date-fns';
import MarkdownContent from 'src/features/product/product-detail/ui/updates-tab/markdown-content/MarkdownContent';
import { AddressBlock } from 'src/shared/components';
import { Sampling, SamplingProductQuote } from 'src/shared/types/sampling';

import LabelValueColumn from 'src/shared/components/LabelValueColumn';
import {
  Link,
  Table,
  TableBody,
  TableCell,
  TableColumn,
  TableHeader,
  TableRow,
  Typography
} from 'src/shared/components/ui';

import samplingQuotesTableColumns from '../../constants/samplingQuotesTableColumns';
import SampleTotalCostWithDiscount from './SampleTotalCostWithDiscount';

interface SamplingModalDetailsProps {
  sampling: Sampling | null;
}

const SamplingModalDetails: React.FC<SamplingModalDetailsProps> = ({
  sampling
}) => {
  const renderCell = useCallback((item: Sampling | null, columnKey: Key) => {
    switch (columnKey) {
      case 'type':
        return (
          <Typography variant="body-1" weight="medium">
            {item?.product_quote?.product_name}
          </Typography>
        );

      case 'price':
        return (
          <Typography variant="body-1" weight="medium">
            {item?.product_quote?.sample_cost_marked_up
              ? `$${item?.product_quote?.sample_cost_marked_up}`
              : null}
          </Typography>
        );

      case 'quantity':
        return (
          <Typography variant="body-1" weight="medium">
            {item?.product_quote?.quantity}
          </Typography>
        );

      case 'supplier':
        return (
          <Typography variant="body-1" weight="medium">
            {item?.cloak}
          </Typography>
        );

      case 'created_at':
        return (
          <Typography variant="body-1" weight="medium">
            {item?.created_at && format(item?.created_at, 'MMM dd, yyyy')}
          </Typography>
        );

      default:
        return '';
    }
  }, []);

  const renderInfoSection = () => {
    if (!sampling?.description && !sampling?.customer_notes) return null;

    return (
      <div className="px-5 py-7 border-b border-gray-4 flex flex-col gap-5">
        <Typography variant="h5" weight="bold">
          Info
        </Typography>

        {sampling?.description && (
          <div className="flex flex-col">
            <Typography
              variant="body-1"
              weight="medium"
              className="text-secondary-text"
            >
              Description
            </Typography>
            <MarkdownContent markdownString={sampling?.description} />
          </div>
        )}

        {sampling?.customer_notes && (
          <div className="flex flex-col">
            <Typography
              variant="body-1"
              weight="medium"
              className="text-secondary-text"
            >
              Notes from the customer
            </Typography>
            <MarkdownContent markdownString={sampling?.customer_notes} />
          </div>
        )}

        {/* <div className="flex flex-col">
        <Typography
          variant="body-1"
          weight="medium"
          className="text-secondary-text"
        >
          Request notes from you
        </Typography>
        <Typography variant="body-1" weight="medium">
          Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do
          eiusmod tempor incididunt ut labore et dolore magna aliqua.
        </Typography>
        <div className="flex items-center gap-[6px] mt-2">
          <UserAvatar name="George" size="sm" />
          <Typography variant="body-1" weight="medium">
            George --
          </Typography>
        </div>
      </div> */}
      </div>
    );
  };

  const renderQaReports = () => {
    const qaFiles = sampling?.uploaded_files?.filter((value) =>
      value?.tags?.includes('qa-report')
    );

    if (qaFiles && qaFiles.length > 0) {
      return (
        <div className="px-5 py-7 border-b border-gray-4 flex flex-col gap-5">
          <Typography variant="h5" weight="bold">
            Quality assurance
          </Typography>

          <div className="flex gap-2">
            {qaFiles.map((file) => (
              <Link key={file.id} to={file.url} isExternal>
                {file.name}
              </Link>
            ))}
          </div>
        </div>
      );
    }

    return null;
  };

  return (
    <div className="flex flex-col">
      {renderInfoSection()}
      {renderQaReports()}

      {sampling?.product_quote && (
        <div className="px-5 py-7 border-b border-gray-4 flex flex-col gap-5">
          <Typography variant="h5" weight="bold">
            Sample cost & delivery
          </Typography>

          <div className="grid grid-cols-2 gap-5">
            <LabelValueColumn
              label="Production cost"
              value={
                sampling?.product_quote?.sample_cost_marked_up
                  ? `$${sampling?.product_quote?.sample_cost_marked_up}`
                  : null
              }
            />
            <LabelValueColumn
              label="Production time"
              value={
                sampling?.product_quote?.sample_production_time_days
                  ? `${sampling?.product_quote?.sample_production_time_days} days`
                  : null
              }
            />
            <LabelValueColumn
              label="Shipping cost"
              value={
                sampling?.product_quote?.sample_shipping_cost_marked_up
                  ? `$${sampling?.product_quote?.sample_shipping_cost_marked_up}`
                  : null
              }
            />

            <LabelValueColumn label="Shipping method" value="Air Freight" />

            <LabelValueColumn
              label="Shipping time"
              value={
                sampling?.product_quote?.sample_shipping_time_days
                  ? `${sampling?.product_quote?.sample_shipping_time_days} days`
                  : null
              }
            />
          </div>

          <div className="grid grid-cols-2 gap-5">
            <LabelValueColumn
              label="Shipping address"
              value={
                sampling?.address ? (
                  <AddressBlock
                    address={sampling?.address}
                    nameWeight="bold"
                    contentWeight="medium"
                    contentClassName="text-primary-text"
                  />
                ) : null
              }
            />
          </div>

          <LabelValueColumn
            label="Total sample cost"
            value={
              <SampleTotalCostWithDiscount
                product_quote={sampling?.product_quote as SamplingProductQuote}
              />
            }
          />
        </div>
      )}

      {sampling?.product_quote && (
        <div className="px-5 py-7 border-b border-gray-4 flex flex-col gap-5 overflow-x-auto">
          <Typography variant="h5" weight="bold">
            Original quote
          </Typography>

          <Table
            aria-label="Original quote"
            removeWrapper
            className="heroui-table heroui-table--small"
          >
            <TableHeader columns={samplingQuotesTableColumns}>
              {(column) => (
                <TableColumn className="px-4" key={column.key}>
                  {column.label}
                </TableColumn>
              )}
            </TableHeader>
            <TableBody items={[sampling]}>
              {(item) => (
                <TableRow key={item?.id}>
                  {(columnKey) => (
                    <TableCell>{renderCell(item, columnKey)}</TableCell>
                  )}
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
};

export default SamplingModalDetails;
